import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

// Icons
import FavoriteIcon from "@mui/icons-material/Favorite";
import WaterIcon from "@mui/icons-material/Water";
import ExploreIcon from "@mui/icons-material/Explore";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PeopleIcon from "@mui/icons-material/People";
import ScienceIcon from "@mui/icons-material/Science";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import VerifiedIcon from "@mui/icons-material/Verified";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SecurityIcon from "@mui/icons-material/Security";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import { PRODUCTS } from "../data/products";

const PRODUCT_ICONS = {
  amyloidconnect: <FavoriteIcon sx={{ fontSize: 48 }} />,
  nephrotrax: <WaterIcon sx={{ fontSize: 48 }} />,
  compass: <ExploreIcon sx={{ fontSize: 48 }} />,
  resourceclan: <VolunteerActivismIcon sx={{ fontSize: 48 }} />,
};

export default function HomePage() {
  const navigate = useNavigate();

  const stats = [
    {
      value: "150+",
      label: "Clinical Trials",
      icon: <ScienceIcon sx={{ fontSize: 40 }} />,
    },
    {
      value: "37M+",
      label: "Patients Served",
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
    },
    {
      value: "3",
      label: "Live Platforms",
      icon: <LocalHospitalIcon sx={{ fontSize: 40 }} />,
    },
    {
      value: "100%",
      label: "Free for Patients",
      icon: <VerifiedIcon sx={{ fontSize: 40 }} />,
    },
  ];

  // Shows all four products, including ResourceClan (In Progress) — the
  // homepage previously only listed AmyloidConnect and NephroTrax at all.
  // Trimmed feature list (4 items) for card-sized display; ProductsPage
  // shows each product's full feature list.
  const products = PRODUCTS.map((p) => ({ ...p, features: p.features.slice(0, 4) }));

  const values = [
    {
      icon: <LightbulbIcon sx={{ fontSize: 50 }} />,
      title: "Innovation",
      description:
        "Building cutting-edge healthcare technology that transforms how patients navigate their medical journey.",
      color: "#f9a825",
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 50 }} />,
      title: "Patient First",
      description:
        "Every feature we build starts with the patient experience. Healthcare should be accessible to everyone.",
      color: "#00897b",
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 50 }} />,
      title: "Trust & Security",
      description:
        "HIPAA-compliant platforms with enterprise-grade security. Your health data is always protected.",
      color: "#1a237e",
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 50 }} />,
      title: "Impact Driven",
      description:
        "Measuring success by the number of patients who find clinical trials and improve their outcomes.",
      color: "#c62828",
    },
  ];

  return (
    <Box>
      {/* HERO SECTION */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a237e 0%, #00897b 100%)",
          color: "white",
          py: { xs: 8, md: 14 },
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'url(\'data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\')',
          },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              {/* <Chip
                label="Healthcare Technology Company"
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  mb: 3,
                  fontWeight: 600,
                }}
              /> */}
              <Typography
                variant="h2"
                fontWeight={800}
                gutterBottom
                sx={{
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                  lineHeight: 1.2,
                }}
              >
                Where Insight
                <Box component="span" sx={{ color: "#f9a825" }}>
                  {" "}
                  Meets Care
                </Box>
              </Typography>
              <Typography
                variant="h5"
                sx={{ opacity: 0.9, mb: 4, lineHeight: 1.6, fontWeight: 400 }}
              >
                Building disease-specific digital health platforms that connect
                patients, providers, and researchers — making complex healthcare
                journeys simpler.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate("/products")}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: "#f9a825",
                    color: "#1a237e",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    "&:hover": { bgcolor: "#f57f17" },
                  }}
                >
                  Our Products
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate("/about")}
                  sx={{
                    color: "white",
                    borderColor: "rgba(255,255,255,0.5)",
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Grid container spacing={2}>
                {stats.map((stat, index) => (
                  <Grid item xs={6} key={index}>
                    <Paper
                      sx={{
                        p: 3,
                        textAlign: "center",
                        bgcolor: "rgba(255,255,255,0.15)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        color: "white",
                        borderRadius: 3,
                      }}
                    >
                      <Box sx={{ color: "#f9a825", mb: 1 }}>{stat.icon}</Box>
                      <Typography variant="h4" fontWeight={800}>
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {stat.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* PRODUCTS SECTION */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#f8fafc" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Chip
              label="Our Platforms"
              sx={{
                bgcolor: "rgba(26,35,126,0.1)",
                color: "#1a237e",
                fontWeight: 600,
                mb: 2,
              }}
            />
            <Typography
              variant="h3"
              fontWeight={700}
              gutterBottom
              sx={{
                background: "linear-gradient(135deg, #1a237e 0%, #00897b 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Disease-Specific Platforms
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: "auto" }}
            >
              Each platform is purpose-built for a specific disease community,
              providing targeted tools and resources.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {products.map((product, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    border: "2px solid",
                    borderColor: "transparent",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: 8,
                      borderColor: product.color,
                    },
                  }}
                >
                  {/* Card Header */}
                  <Box
                    sx={{
                      background: product.gradient,
                      p: 4,
                      color: "white",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box sx={{ color: "rgba(255,255,255,0.9)" }}>
                        {PRODUCT_ICONS[product.key]}
                      </Box>
                      <Chip
                        label={product.status}
                        color={product.statusColor}
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                    </Box>
                    <Typography variant="h4" fontWeight={700} sx={{ mt: 2 }}>
                      {product.name}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      {product.tagline}
                    </Typography>
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      paragraph
                    >
                      {product.description}
                    </Typography>

                    {/* Features */}
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}
                    >
                      {product.features.map((feature, idx) => (
                        <Chip
                          key={idx}
                          label={feature}
                          size="small"
                          sx={{
                            bgcolor: `${product.color}15`,
                            color: product.color,
                            fontWeight: 600,
                          }}
                        />
                      ))}
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      🌐 {product.url}
                    </Typography>

                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={() => navigate(product.path)}
                      sx={{
                        background: product.gradient,
                        fontWeight: 700,
                        py: 1.5,
                        "&:hover": { opacity: 0.9 },
                      }}
                    >
                      {product.status === "Live" ? "Learn More" : "Coming Soon"}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* VALUES SECTION */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "white" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Chip
              label="Our Values"
              sx={{
                bgcolor: "rgba(0,137,123,0.1)",
                color: "#00897b",
                fontWeight: 600,
                mb: 2,
              }}
            />
            <Typography variant="h3" fontWeight={700} gutterBottom>
              What Drives Us
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: "auto" }}
            >
              Every decision we make is guided by our core values and commitment
              to patient care.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    textAlign: "center",
                    p: 1,
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
                  }}
                >
                  <CardContent>
                    <Box sx={{ color: value.color, mb: 2 }}>{value.icon}</Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      {value.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7 }}
                    >
                      {value.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA SECTION */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: "linear-gradient(135deg, #1a237e 0%, #00897b 100%)",
          color: "white",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Ready to Transform Healthcare?
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 4 }}>
            Whether you're a patient seeking trials, a provider looking for
            tools, or an organization interested in partnership — we're here to
            help.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/products")}
              sx={{
                bgcolor: "#f9a825",
                color: "#1a237e",
                fontWeight: 700,
                px: 4,
                py: 1.5,
                "&:hover": { bgcolor: "#f57f17" },
              }}
            >
              Explore Our Products
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate("/contact")}
              sx={{
                color: "white",
                borderColor: "rgba(255,255,255,0.5)",
                fontWeight: 600,
                px: 4,
                py: 1.5,
                "&:hover": {
                  borderColor: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}